import axios, {
  type AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authToken } from './auth-token';
import { APIError, type APIErrorPayload } from './error';

// Accept both VITE_API_BASE_URL (frontend convention) and VITE_API_URL
// (the name the Dockerfile passes). Fallback to relative /api/v1.
const baseURL =
  import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? '/api/v1';

export const apiClient = axios.create({
  baseURL,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: attach access token ────────────────────────────
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const tokens = authToken.read();
  if (tokens?.accessToken) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
    config.headers = headers;
  }
  return config;
});

// ─── Refresh-mutex so concurrent 401s don't trigger multiple refreshes ───
let refreshPromise: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  const tokens = authToken.read();
  if (!tokens?.refreshToken) return null;
  try {
    const res = await axios.post<{
      access_token: string;
      refresh_token?: string;
      access_expires_at?: string;
    }>(
      `${baseURL.replace(/\/$/, '')}/auth/refresh`,
      { refresh_token: tokens.refreshToken },
      { timeout: 15_000 },
    );
    authToken.write({
      accessToken: res.data.access_token,
      refreshToken: res.data.refresh_token ?? tokens.refreshToken,
      accessExpiresAt: res.data.access_expires_at ?? null,
    });
    return res.data.access_token;
  } catch {
    authToken.clear();
    return null;
  }
}

// ─── Response interceptor: normalise + handle 401 → refresh → retry ──────
apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<APIErrorPayload>) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      if (!refreshPromise) refreshPromise = tryRefresh().finally(() => (refreshPromise = null));
      const newToken = await refreshPromise;
      if (newToken) {
        const headers = AxiosHeaders.from(original.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        original.headers = headers;
        return apiClient.request(original);
      }
      // Hard logout — surface to caller. Page-level guard handles redirect.
      window.dispatchEvent(new CustomEvent('syron:auth:logout'));
    }

    const status = error.response?.status ?? 0;
    const payload = error.response?.data;
    throw new APIError(status, payload, error.message);
  },
);

// Typed helpers — keep call-sites concise.
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.get<T>(url, config);
  return res.data;
}
export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<T>(url, body, config);
  return res.data;
}
export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.put<T>(url, body, config);
  return res.data;
}
export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.patch<T>(url, body, config);
  return res.data;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.delete<T>(url, config);
  return res.data;
}
